<script setup>
import Buttons from '@/components/ui/Buttons.vue';
defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
</script>
<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      data?.background_color ? `bg-${data?.background_color}` : '',
      data?.spacing?.top
        ? `section--top-${data.spacing.top}`
        : 'section--top-xl',
      data?.spacing?.bottom
        ? `section--bottom-${data.spacing.bottom}`
        : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        class="steps"
        :class="[
          data?.background_color
            ? `steps steps--${data.background_color}`
            : 'steps',
        ]"
      >
        <h2
          v-if="data?.title"
          class="mb-0 steps__title h2"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data?.title"
        ></h2>
        <ul
          v-if="data.items"
          :class="[`m-0 p-0 steps__items steps__items--${data?.columns_count}`]"
        >
          <li
            v-for="(step, stepIndex) in data.items"
            :key="step.number"
            class="m-0 steps__item"
            data-aos="fade-up"
            :data-aos-delay="stepIndex >= 1 ? 100 + 100 * stepIndex : 100"
          >
            <span
              v-if="step.number"
              class="steps__item-number h3"
              v-html="step.number"
            ></span>
            <div class="steps__item-content">
              <p
                v-if="step.title"
                class="mb-0 steps__item-title h6-mobile"
                v-html="step.title"
              ></p>
              <div
                v-if="step.description"
                class="mb-0 steps__item-text body-3-regular"
                v-html="step.description"
              ></div>
            </div>
          </li>
        </ul>
        <div
          v-if="data.note"
          class="steps__note body-3-regular"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data.note"
        ></div>
        <Buttons
          v-if="data?.buttons && data?.buttons.length > 0"
          :buttons="data?.buttons"
          :background-color="data?.background_color"
          class="steps__buttons"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          :buttons-prefix-class="'js-button-steps'"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.steps {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &--white {
    #{$parent} {
    }
  }
  &--black {
    #{$parent} {
      &__title {
        color: $white;
      }
      &__item {
        &:after {
          background-color: $on-surface-variant-2;
        }
        &-number {
          background-color: rgba($surface-container-opacity, 0.5);
        }
        &-title {
          color: $white;
        }
        &-text {
          color: $gray;
        }
      }
      &__note {
        color: $gray;
      }
    }
  }
  &--gray {
  }
  &__title {
    margin: 0;
    text-align: center;
  }
  &__items {
    display: grid;
    grid-template-columns: 1fr;
    gap: 56px;
    list-style: none;
    @include media-breakpoint-up(sm) {
      gap: 80px;
      margin: 0 auto;
      width: 66%;
    }
    @include media-breakpoint-up(md) {
      width: 100%;
      &--2 {
        grid-template-columns: repeat(2, 1fr);
      }
      &--3 {
        grid-template-columns: repeat(3, 1fr);
      }
      &--4 {
        grid-template-columns: repeat(4, 1fr);
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
    }
  }
  &__item {
    align-content: start;
    display: grid;
    gap: 24px;
    grid-template-columns: 72px auto;
    position: relative;
    &:after {
      background-color: $divider;
      content: '';
      height: calc(100% - 48px);
      left: 36px;
      position: absolute;
      top: 88px;
      width: 1px;
    }
    @include media-breakpoint-up(sm) {
      gap: 48px;
      grid-template-columns: 88px auto;
      &:after {
        height: calc(100% - 40px);
        left: 44px;
        top: 104px;
      }
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 1fr;
      justify-items: center;
      text-align: center;
      &:after {
        height: 1px;
        left: 85%;
        top: 42px;
        width: calc(30% + 80px);
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
      &:after {
        @include responsive-spacing(height, 1);
        @include responsive-spacing(top, 42);
        width: calc(30% + px-to-vw(80));
      }
    }
    &:last-child {
      &::after {
        display: none;
      }
    }
    &-number {
      align-items: center;
      background-color: $white;
      border-radius: 2px;
      color: $primary;
      display: flex;
      justify-content: center;
      height: 72px;
      width: 72px;
      @include media-breakpoint-up(sm) {
        height: 88px;
        width: 88px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 88);
        @include responsive-spacing(width, 88);
        @include responsive-spacing(border-radius, 2);
      }
    }
    &-content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 16px;
      @include media-breakpoint-up(md) {
        justify-content: flex-start;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
    }
    &-title {
      color: $black;
    }
    &-text {
      color: $on-surface-variant-2;
    }
  }
  &__note {
    color: $on-surface-variant-2;
    margin: 0 auto;
    text-align: center;
    @include media-breakpoint-up(sm) {
      width: 50%;
    }
    &:deep(p:last-child) {
      margin: 0;
    }
  }
  &__buttons {
    justify-content: center;
  }
}
</style>
